import React, { useState, useEffect } from 'react';
import { toast } from './Toast';
import { askConfirm } from './ui';
import { FileText, Download, Trash2, Eye, ExternalLink, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface Document {
  id: number;
  file_name: string;
  file_size: number;
  mime_type: string;
  gdrive_file_id: string;  // ZMIANA: drive_file_id → gdrive_file_id
  gdrive_link: string;      // NOWE: bezpośredni link do pliku
  created_at: string;       // ZMIANA: upload_date → created_at
  description?: string;
}

interface Props {
  applicationId: number;
  refreshTrigger?: number;
}

export const DocumentsList: React.FC<Props> = ({ applicationId, refreshTrigger }) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDocuments();
  }, [applicationId, refreshTrigger]);

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('grant_crm_token');
      const response = await axios.get(`/api/documents/list/${applicationId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      setDocuments(response.data.documents || []);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      setError(error.response?.data?.error || 'Błąd podczas ładowania dokumentów');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentId: number) => {
    if (!(await askConfirm({ title: 'Czy na pewno chcesz usunąć ten dokument?' }))) return;

    try {
      const token = localStorage.getItem('grant_crm_token');
      await axios.delete(`/api/documents/${documentId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });

      toast.info('Dokument usunięty');
      loadDocuments();
    } catch (error: any) {
      console.error('Error deleting document:', error);
      toast.error(error.response?.data?.error || 'Błąd podczas usuwania dokumentu');
    }
  };

  const handleView = (doc: Document) => {
    // Open Google Drive file in new tab using direct link from backend
    if (doc.gdrive_link) {
      window.open(doc.gdrive_link, '_blank');
    } else {
      // Fallback construction if link not available
      const driveUrl = `https://drive.google.com/file/d/${doc.gdrive_file_id}/view`;
      window.open(driveUrl, '_blank');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL') + ' ' + date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('zip') || mimeType.includes('rar')) return '🗜️';
    return '📎';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Ładowanie dokumentów...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border-2 border-red-200 p-6">
        <p className="text-red-600">⚠️ {error}</p>
        <button
          onClick={loadDocuments}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
        >
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Brak dokumentów</p>
          <p className="text-sm text-gray-500 mt-1">Dodaj pliki używając panelu upload powyżej</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Dokumenty ({documents.length})
        </h3>
        <button
          onClick={loadDocuments}
          className="text-blue-600 hover:text-blue-700 transition flex items-center gap-1 text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Odśwież
        </button>
      </div>

      <div className="space-y-3">
        {documents.map(doc => (
          <div
            key={doc.id}
            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition"
          >
            <span className="text-3xl">{getFileIcon(doc.mime_type)}</span>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-medium text-gray-900 truncate">{doc.file_name}</span>
                <span className="text-sm text-gray-500 flex-shrink-0">
                  {formatFileSize(doc.file_size)}
                </span>
              </div>

              {doc.description && (
                <p className="text-sm text-gray-600 truncate mb-1">{doc.description}</p>
              )}

              <p className="text-xs text-gray-500">
                Dodano: {formatDate(doc.created_at)}
              </p>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => handleView(doc)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                title="Otwórz w Google Drive"
              >
                <Eye className="w-5 h-5" />
              </button>

              <button
                onClick={() => handleView(doc)}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                title="Otwórz w Google Drive"
              >
                <ExternalLink className="w-5 h-5" />
              </button>

              <button
                onClick={() => handleDelete(doc.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                title="Usuń dokument"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-900">
          ℹ️ Dokumenty są przechowywane na Google Drive. Kliknij ikonę oka aby otwórzyć dokument.
        </p>
      </div>
    </div>
  );
};
